import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthContext } from "@/auth/AuthProvider";
import { fetchExceptionsSummary } from "@/api/exceptions";
import { QUERY_KEYS } from "@/config/constants";

const relativeFormatter = new Intl.RelativeTimeFormat("en", {
  numeric: "always",
  style: "short",
});

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60)
    return relativeFormatter.format(-seconds, "second").replaceAll(".", "");
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
    return relativeFormatter.format(-minutes, "minute").replaceAll(".", "");
  const hours = Math.floor(minutes / 60);
  return relativeFormatter.format(-hours, "hour").replaceAll(".", "");
}

export function useLastRefreshed(): string {
  const { user } = useAuthContext();

  const { dataUpdatedAt } = useQuery({
    queryKey: [...QUERY_KEYS.EXCEPTIONS.summary, user?.activeGroupId],
    queryFn: () => fetchExceptionsSummary(user!.activeGroupId),
    enabled: user !== null,
    refetchInterval: 10_000,
    select: (response) => ({
      checksum: response.checksum,
      total: response.summary.reduce((sum, cat) => sum + cat.total, 0),
    }),
  });

  const [timeAgo, setTimeAgo] = useState("");

  useEffect(() => {
    if (!dataUpdatedAt) return;

    const update = () => setTimeAgo(formatTimeAgo(dataUpdatedAt));
    update();

    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  return timeAgo;
}
